import { useQuery } from '@tanstack/react-query';
import { getVideo } from '@/services/videoApi';

/**
 * Hook to fetch video metadata
 *
 * This hook fetches metadata for a specific video including:
 * - Duration, frame count, FPS
 * - Video dimensions (width, height)
 * - File size and other metadata
 *
 * Uses React Query for caching and automatic refetching.
 *
 * @param videoId - The ID of the video to fetch metadata for
 * @returns Query result with video metadata
 *
 * @example
 * ```tsx
 * const { data: video, isLoading, error } = useVideoMetadata(videoId);
 *
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error loading video</div>;
 *
 * return <div>Duration: {video.duration}s</div>;
 * ```
 */
export function useVideoMetadata(videoId: string | undefined) {
  return useQuery({
    queryKey: ['video-metadata', videoId],
    queryFn: () => getVideo(videoId!),
    enabled: !!videoId,
  });
}
